import React, { useCallback, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../application/store";
import { loadProducts } from "../../../application/products-slice";
import { ProductsRepository, Sort, sortFormString } from "../../../domain";
import { useQuery } from "../../hooks";
import {
  Button,
  Card,
  List,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Typography,
} from "antd";
import I18n from "../../i18n";
import { useHistory, useLocation } from "react-router-dom";
import { DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { ServiceLocater, Services } from "../../../service-locater";

const { Title, Text } = Typography;

export default function Products() {
  const query = useQuery();
  const history = useHistory();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { loading, pageSize, productsCount, products } = useAppSelector(
    (state) => state.products
  );

  let page: number;
  try {
    const pageStr = query.get("page");
    page = parseInt(pageStr === null ? "1" : pageStr);
  } catch (_) {
    page = 1;
  }

  let selectedSort: Sort;
  const sortJson = query.get("sort");
  if (sortJson !== null) selectedSort = sortFormString(sortJson);
  else selectedSort = Sort.date;

  const stableDispatch = useCallback(dispatch, []);
  useEffect(() => {
    stableDispatch(loadProducts(page, selectedSort));
  }, [stableDispatch, page, selectedSort]);

  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const deleteProduct = async (id: string) => {
    setIsDeleteLoading(true);
    const hide = message.loading("در حال حذف محصول مورد نظر...");
    const res = await ServiceLocater.get<ProductsRepository>(
      Services.ProductsRepository
    ).delete(id);
    setIsDeleteLoading(false);
    hide();
    res.fold(
      (_) => {
        message.error(I18n.general.networkError);
      },
      (_) => {
        message.success(I18n.product.deleteSuccess);
        dispatch(loadProducts(page, selectedSort));
      }
    );
  };

  const [attributesModal, setAttributesModal] = useState<{
    id?: string;
    visible: boolean;
  }>({ visible: false });

  const isLoading = loading !== "succeeded";
  return (
    <div>
      <List
        style={{ paddingBottom: 8 }}
        dataSource={products}
        loading={isLoading}
        header={
          <Row style={{ padding: "4px 16px" }}>
            <Typography.Text>{I18n.productsList.sortBy}:</Typography.Text>
            <Select
              size="small"
              defaultValue={selectedSort}
              dropdownMatchSelectWidth
              bordered={false}
              onSelect={(value, _) => {
                query.set("sort", value);
                history.push({
                  pathname: location.pathname,
                  search: query.toString(),
                });
              }}
            >
              <Select.Option value={Sort.date}>
                {I18n.productsList.sort.date}
              </Select.Option>
              <Select.Option value={Sort.dateAscending}>
                {I18n.productsList.sort.dateAscending}
              </Select.Option>
              <Select.Option value={Sort.priceAscending}>
                {I18n.productsList.sort.priceAscending}
              </Select.Option>
              <Select.Option value={Sort.priceDescending}>
                {I18n.productsList.sort.priceDescending}
              </Select.Option>
            </Select>
          </Row>
        }
        renderItem={(item, index) => {
          return (
            <List.Item
              style={{
                padding: "8px 16px",
                borderBottom: "1px solid #0000001a",
              }}
              actions={[
                <Button
                  type="link"
                  size="middle"
                  disabled={isDeleteLoading}
                  onClick={() => history.push(`/edit-product?id=${item.id}`)}
                >
                  <EditOutlined />
                </Button>,
                <Popconfirm
                  onConfirm={() => deleteProduct(item.id.toString())}
                  title={I18n.product.deleteConfirm}
                  placement="rightTop"
                >
                  <Button
                    type="link"
                    size="middle"
                    danger
                    disabled={isDeleteLoading}
                  >
                    <DeleteOutlined />
                  </Button>
                </Popconfirm>,
              ]}
            >
              <Row
                onClick={() =>
                  setAttributesModal({ visible: true, id: item.id.toString() })
                }
              >
                <img
                  src={item.images[0].url}
                  alt={item.title}
                  width={100}
                  height="auto"
                />
                <div style={{ marginRight: 16 }}>
                  <Title level={5}>{item.title}</Title>
                </div>
              </Row>
            </List.Item>
          );
        }}
        pagination={{
          pageSize,
          total: productsCount,
          position: "bottom",
          style: { display: "table", margin: "0 auto" },
          onChange: (page, _) => {
            query.set("page", page.toString());
            history.push({
              pathname: location.pathname,
              search: query.toString(),
            });
          },
        }}
      />

      <Modal
        visible={attributesModal.visible}
        onCancel={() => setAttributesModal({ visible: false, id: undefined })}
      >
        {products
          .find((product) => product.id.toString() === attributesModal.id)
          ?.attributes.map((attribute, index) => {
            return (
              <Card
                bodyStyle={{ padding: 16 }}
                style={index === 0 ? {} : { marginTop: 8 }}
              >
                <Row>
                  <div>
                    <Text>{I18n.product.color}</Text>
                    <br />
                    <Text>{I18n.product.size}</Text>
                    <br />
                    <Text>{I18n.product.sizeDescription}</Text>
                    <br />
                    <Text>{I18n.product.count}</Text>
                    <br />
                    <Text>{I18n.product.price}</Text>
                    <br />
                    <Text>{I18n.product.price} تخفیف</Text>
                  </div>
                  <div style={{ paddingRight: 16 }}>
                    <Text>{attribute.color.title}</Text>
                    <br />
                    <Text>{attribute.size}</Text>
                    <br />
                    <Text>{attribute.sizeDescription}</Text>
                    <br />
                    <Text>{attribute.count}</Text>
                    <br />
                    <Text>{attribute.price.toLocaleString()} تومان</Text>
                    <br />
                    <Text>{attribute.offPrice?.toLocaleString()} تومان</Text>
                  </div>
                </Row>
              </Card>
            );
          })}
      </Modal>
    </div>
  );
}
